"use client";
import Link from "next/link";
import Marquee from "react-fast-marquee";
import { useState, useEffect } from "react";
import { useTheme, UseThemeProps } from "next-themes";
import { useContactDrawer } from "./contact-drawer-provider";

interface WorkStatusProps {
  theme?: UseThemeProps["theme"]
  display?: "static" | "marquee";
  status?: "available" | "unavailable";
  href?: string;
  bgColor?: string;
  availableLabel?: string;
  unavailableLabel?: string;
}

function StatusItem({ 
  theme = "dark", 
  status = "available",
  availableLabel = "AVAILABLE FOR WORK",
  unavailableLabel = "NOT AVAILABLE FOR WORK",
  isStatic = false
}: { 
  theme?: UseThemeProps["theme"]; 
  status?: "available" | "unavailable";
  availableLabel?: string;
  unavailableLabel?: string;
  isStatic?: boolean;
}) {
  const statusText = status === "available" ? availableLabel : unavailableLabel;
  const dotColor = status === "available" ? "bg-green" : "bg-red";
  const pulseColor = status === "available" 
    ? "rgba(125, 164, 124, 0.5)" 
    : "rgba(167, 49, 51, 0.5)";

  // Responsive classes for static variant
  if (isStatic) {
    return (
      <span className="text-xs sm:text-sm md:text-base flex items-center gap-2 whitespace-nowrap">
        <div 
          className={`status-pulse min-h-2 min-w-2 h-2.5 w-2.5 sm:h-3 sm:w-3 md:h-3.5 md:w-3.5 ${dotColor} rounded-full flex-shrink-0 animate-status-pulse`}
        ></div>
        <span className="leading-tight font-medium">{statusText}</span>
      </span>
    );
  }

  // Marquee variant - unchanged
  return (
    <span className="text-xs sm:text-sm flex items-center gap-1 sm:gap-1.5 whitespace-nowrap">
      <div 
          className={`status-pulse min-h-2 min-w-2 h-2 w-2 sm:h-2.5 sm:w-2.5 ${dotColor} rounded-full flex-shrink-0 animate-status-pulse`}
      ></div>
      <span>{statusText}</span>
    </span>
  );
}

export default function WorkStatus({ 
  theme, 
  display = "marquee", 
  status = "available",
  href = "/contact",
  bgColor,
  availableLabel = "AVAILABLE FOR WORK",
  unavailableLabel = "NOT AVAILABLE FOR WORK"
}: WorkStatusProps) {
  const { theme: systemTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const { openDrawer } = useContactDrawer();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Use provided theme, or resolved theme (accounts for system preference)
  // During SSR and before mount, default to "light" to avoid hydration mismatch
  const currentTheme: UseThemeProps["theme"] = theme || (mounted && resolvedTheme ? (resolvedTheme as UseThemeProps["theme"]) : "light");

  const marqueeItems = Array.from({ length: 3 });

  // Use provided bgColor or default based on theme
  const defaultBgColor = currentTheme === "light" ? "bg-transparent" : "bg-transparent";
  const finalBgColor = bgColor || defaultBgColor;

  // Responsive container classes - different for static vs marquee
  // Improved padding and styling for better appearance
  const staticContainerClasses = `${finalBgColor} py-2 sm:py-2.5 md:py-2.5 px-4 sm:px-5 md:px-6 lg:px-7 overflow-hidden rounded-full cursor-pointer border-2 text-xs sm:text-sm md:text-base flex items-center justify-center gap-2 transition-all hover:scale-105 ${
    currentTheme === "light" 
      ? "text-espresso border-espresso bg-beige shadow-sm" 
      : "text-white border-white/30 bg-coffee/50 backdrop-blur-sm"
  }`;

  const marqueeContainerClasses = `${finalBgColor} py-2 sm:py-2.5 md:py-2.5 overflow-hidden rounded-full cursor-pointer border-2 text-xs sm:text-sm flex items-center ${
    currentTheme === "light" 
      ? "text-espresso border-espresso bg-beige shadow-sm" 
      : "text-white border-white/30 bg-coffee/50 backdrop-blur-sm"
  }`;

  const renderContent = () => {
    if (display === "static") {
      return (
        <StatusItem 
          theme={currentTheme} 
          status={status}
          availableLabel={availableLabel}
          unavailableLabel={unavailableLabel}
          isStatic={true}
        />
      );
    }

    return (
      <Marquee className="rounded-full overflow-hidden flex items-center">
        {marqueeItems.map((_, idx) => (
          <div className="marquee me-2 px-7 flex items-center" key={idx}>
            <StatusItem 
              theme={currentTheme} 
              status={status}
              availableLabel={availableLabel}
              unavailableLabel={unavailableLabel}
              isStatic={false}
            />
          </div>
        ))}
      </Marquee>
    );
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (href === "/contact") {
      e.preventDefault();
      openDrawer();
    }
  };

  if (display === "static") {
    if (href === "/contact") {
      return (
        <button
          type="button"
          onClick={handleClick}
          onPointerDown={(e) => {
            if (href === "/contact") {
              e.preventDefault();
              openDrawer();
            }
          }}
          className="inline-block min-h-[44px] touch-manipulation cursor-pointer relative z-10"
          suppressHydrationWarning
        >
          <div className={staticContainerClasses} suppressHydrationWarning>
            {renderContent()}
          </div>
        </button>
      );
    }
    return (
      <Link
        href={href}
        passHref
        suppressHydrationWarning
        className="inline-block"
      >
        <div className={staticContainerClasses} suppressHydrationWarning>
          {renderContent()}
        </div>
      </Link>
    );
  }

  if (href === "/contact") {
    return (
      <div className="md:block">
        <button
          type="button"
          className="w-40 sm:w-64 absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2"
          onClick={handleClick}
          suppressHydrationWarning
        >
          <div className={marqueeContainerClasses} suppressHydrationWarning>
            {renderContent()}
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="md:block">
      <Link
        className="w-40 sm:w-64 absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        href={href}
        passHref
        suppressHydrationWarning
      >
        <div className={marqueeContainerClasses} suppressHydrationWarning>
          {renderContent()}
        </div>
      </Link>
    </div>
  );
}